import { isTag, isText, type ChildNode, type Element } from 'domhandler';
import { normalisePreviewTextArtifacts } from './signedUrlPreviewUtils';
import {
  getNodeText,
  getTagChildren,
  parseDocrefDocument,
} from './document-reference/document-reference-dom';

type ProcedureNoteBlock =
  | { kind: 'text'; text: string }
  | { kind: 'table'; headers: string[]; rows: string[][] };

interface ProcedureNoteContentPayload {
  blocks: ProcedureNoteBlock[];
}

interface ProcedureNoteContentProps {
  procedureNote?: string;
}

const HTML_ENTITY_PATTERN = /&(?:[A-Za-z][\w]+|#\d+|#x[\dA-Fa-f]+);/;
const MARKUP_PATTERN = /<\/?[A-Za-z][^>]*>/;
const XHTML_DIV_PATTERN =
  /^<div[^>]*xmlns=(["'])http:\/\/www\.w3\.org\/1999\/xhtml\1[^>]*>([\s\S]*)<\/div>$/i;

const TEXT_TAG_NAMES = new Set(['caption', 'content', 'paragraph', 'p']);
const CONTAINER_TAG_NAMES = new Set([
  'list',
  'item',
  'td',
  'tr',
  'div',
  'tbody',
  'thead',
]);

const normaliseWhitespace = (value: string) =>
  normalisePreviewTextArtifacts(
    value
      .replace(/\r\n/g, '\n')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .join('\n')
      .trim(),
  );

const looksLikeMarkup = (value: string) => MARKUP_PATTERN.test(value);

const decodeHtmlEntities = (value: string) => {
  if (typeof document === 'undefined') {
    return value
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&apos;/g, "'")
      .replace(/&amp;/g, '&');
  }

  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
};

const decodeIfEncodedMarkup = (text: string) => {
  if (!HTML_ENTITY_PATTERN.test(text) || looksLikeMarkup(text)) return text;

  const decoded = decodeHtmlEntities(text);
  return looksLikeMarkup(decoded) ? decoded : text;
};

/** HTML-style `<br>` → `<br/>` so xmlMode keeps text after the break as siblings. */
const normalizeVoidBreakTags = (value: string) =>
  value.replace(/<br\b([^>]*?)>/gi, (match, attrs: string | undefined) => {
    const trimmed = (attrs ?? '').trim();
    if (trimmed.endsWith('/')) return match;
    return `<br${attrs ?? ''}/>`;
  });

const prepareProcedureNoteMarkup = (raw: string) => {
  let current = normalizeVoidBreakTags(stripXmlDeclaration(raw.trim()));
  if (!current) return '';

  for (let depth = 0; depth < 3; depth += 1) {
    const decoded = decodeIfEncodedMarkup(current);
    const unwrapped = unwrapXhtmlDiv(decoded);
    const next = normalizeVoidBreakTags(unwrapped);
    if (next === current) {
      return next;
    }
    current = next;
  }

  return current;
};

const stripXmlDeclaration = (value: string) =>
  value.replace(/^<\?xml[\s\S]*?\?>\s*/i, '');

const unwrapXhtmlDiv = (value: string) => {
  const trimmed = value.trim();
  const match = trimmed.match(XHTML_DIV_PATTERN);
  return match ? match[2].trim() : trimmed;
};

const getTableHeaders = (table: Element) => {
  const thead = getTagChildren(table).find(
    (child) => child.name.toLowerCase() === 'thead',
  );
  if (!thead) return [];

  const headerRow = getTagChildren(thead).find(
    (child) => child.name.toLowerCase() === 'tr',
  );
  if (!headerRow) return [];

  return getTagChildren(headerRow)
    .filter((cell) => ['th', 'td'].includes(cell.name.toLowerCase()))
    .map((cell) => normaliseWhitespace(getNodeText(cell.children)))
    .filter(Boolean);
};

const getTableBodyRows = (table: Element) => {
  const tbody = getTagChildren(table).find(
    (child) => child.name.toLowerCase() === 'tbody',
  );
  const rowNodes = tbody
    ? getTagChildren(tbody).filter((child) => child.name.toLowerCase() === 'tr')
    : getTagChildren(table).filter(
        (child) => child.name.toLowerCase() === 'tr',
      );

  return rowNodes
    .map((row) =>
      getTagChildren(row)
        .filter((cell) => ['th', 'td'].includes(cell.name.toLowerCase()))
        .map((cell) => normaliseWhitespace(getNodeText(cell.children))),
    )
    .filter((row) => row.some(Boolean));
};

const parseGenericTable = (table: Element): ProcedureNoteBlock | undefined => {
  const headers = getTableHeaders(table);
  const rows = getTableBodyRows(table);
  if (rows.length === 0) return undefined;

  return {
    kind: 'table',
    headers,
    rows,
  };
};

const appendUniqueBlock = (
  blocks: ProcedureNoteBlock[],
  block: ProcedureNoteBlock | undefined,
) => {
  if (!block) return;

  if (block.kind === 'text') {
    const text = normaliseWhitespace(block.text);
    if (!text) return;
    const previous = blocks.at(-1);
    if (previous?.kind === 'text' && previous.text === text) return;
    blocks.push({ kind: 'text', text });
    return;
  }

  blocks.push(block);
};

const collectBlocks = (
  nodes: ReadonlyArray<ChildNode>,
  blocks: ProcedureNoteBlock[],
) => {
  nodes.forEach((node) => {
    if (isText(node)) {
      appendUniqueBlock(blocks, {
        kind: 'text',
        text: normaliseWhitespace(node.data),
      });
      return;
    }

    if (!isTag(node)) return;

    const tagName = node.name.toLowerCase();
    if (tagName === 'colgroup') return;

    if (tagName === 'table') {
      appendUniqueBlock(blocks, parseGenericTable(node));
      return;
    }

    if (TEXT_TAG_NAMES.has(tagName)) {
      appendUniqueBlock(blocks, {
        kind: 'text',
        text: normaliseWhitespace(getNodeText(node.children)),
      });
      return;
    }

    if (CONTAINER_TAG_NAMES.has(tagName) || tagName === 'span') {
      collectBlocks(node.children, blocks);
    }
  });
};

const parseProcedureNoteMarkup = (
  raw?: string,
): ProcedureNoteContentPayload | undefined => {
  const markup = prepareProcedureNoteMarkup(raw?.trim() ?? '');
  if (!markup) return undefined;

  if (!looksLikeMarkup(markup)) {
    const text = normaliseWhitespace(markup);
    return text ? { blocks: [{ kind: 'text', text }] } : undefined;
  }

  const document = parseDocrefDocument(markup);
  const blocks: ProcedureNoteBlock[] = [];
  collectBlocks(document.children, blocks);

  return blocks.length > 0 ? { blocks } : undefined;
};

const ProcedureNoteTable = ({
  headers,
  rows,
}: {
  headers: string[];
  rows: string[][];
}) => {
  const columnCount = Math.max(
    headers.length,
    ...rows.map((row) => row.length),
    1,
  );

  return (
    <div className="overflow-x-auto rounded-sm border border-zinc-200">
      <table className="min-w-full border-collapse text-[10px] leading-tight">
        {headers.length > 0 ? (
          <thead>
            <tr className="bg-slate-100">
              {headers.map((header, idx) => (
                <th
                  key={`procedure-note-header-${idx}`}
                  className="border-r border-zinc-300 px-2 py-1.5 text-left font-black tracking-[0.35px] text-zinc-500 uppercase last:border-r-0"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
        ) : null}
        <tbody>
          {rows.map((row, rowIdx) => (
            <tr
              key={`procedure-note-row-${rowIdx}`}
              className={rowIdx % 2 === 1 ? 'bg-gray-50' : 'bg-white'}
            >
              {Array.from({ length: columnCount }, (_, colIdx) => (
                <td
                  key={`procedure-note-cell-${rowIdx}-${colIdx}`}
                  className="border-t border-r border-zinc-200 px-2 py-1.5 align-top whitespace-pre-line text-zinc-950 last:border-r-0"
                >
                  {row[colIdx] ?? ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const ProcedureNoteContent = ({
  procedureNote,
}: ProcedureNoteContentProps) => {
  const parsed = parseProcedureNoteMarkup(procedureNote);
  if (!parsed?.blocks.length) return null;

  return (
    <div className="flex flex-col gap-2">
      {parsed.blocks.map((block, idx) => {
        if (block.kind === 'text') {
          return (
            <p
              key={`procedure-note-text-${idx}`}
              className="text-[10px] leading-tight whitespace-pre-line text-zinc-950"
            >
              {block.text}
            </p>
          );
        }

        return (
          <ProcedureNoteTable
            key={`procedure-note-table-${idx}`}
            headers={block.headers}
            rows={block.rows}
          />
        );
      })}
    </div>
  );
};
